import { forwardRef } from 'react';

import { Field } from '@base-ui/react/field';
import { IconButton } from 'components/Button';
import { cn } from 'components/utils';

import {
  textInputHelperRecipe,
  textInputIconRecipe,
  textInputPillBoxRecipe,
  textInputPillControlRecipe,
  textInputPillRootRecipe,
  textInputPillTrailingButtonRecipe,
} from './TextInput.recipe';

import type { TextInputPillProps } from './types';

/**
 * Design System 2.0 text input — the pill variant.
 *
 * Ported from Figma node 487:38319, which is a SEPARATE component set from the 60px input rather
 * than a variant of it, and differs in more than its radius:
 *
 *   - 42px tall against 60px. Both shapes now draw their value at the scale's default reading
 *     size; the pill was a step smaller until the type scale landed.
 *   - no floating label. The "Label" in the design sits on the value line and does not move, which
 *     makes it a placeholder. The accessible name therefore comes from a visually hidden
 *     `Field.Label` — a placeholder is not a name, and disappears the moment the user types.
 *   - helper text sits BELOW the border rather than inside it.
 *   - no Corner Radius axis, so no `position` and no grouping. Stacking pills flush would defeat
 *     the shape.
 *
 * What does NOT differ any more is the colour. The pill reads the same semantic roles as the 60px
 * shape through its own `--ds-input-pill-*` family — the recessed control well, the hover fill, the
 * 2px accent focus border, no drawn halo — so the two shapes side by side are the same material in
 * the same states.
 *
 * That parity used to be a documented DEPARTURE from the pill's own Figma set, which the library
 * had not reworked. It is now a match: `Text Input_Pill` (487:38319) was rebuilt against
 * `Text Input` (447:35126) and draws the control well, the hover fill and the 2px accent Active
 * border itself, on the shared `#1372:*` property keys and with its states split into `isFilled` /
 * `isDisabled` / `hasError` rather than folded into one `State` list. `design-system.css` still
 * records what each cell cost, which is now history rather than an open divergence.
 *
 * Two bullets were deleted from the list above rather than edited, and they are named here so
 * neither is reinstated from a reading of Figma:
 *
 *   - the pill used to draw no border in dark mode until focused or errored, separating from the
 *     page by fill alone. It has a boundary in both themes now.
 *   - the trailing affordance used to be a bare glyph. It is the same `IconButton` the 60px field
 *     renders — see `TextInputPillProps` for why the split closed.
 *
 * ── Why the 42px height does not move, even though Figma's padding changed ────────────────────
 *
 * The rebuilt set pays 8px of FRAME padding around a 24px trailing cell (`1+8+24+8+1 = 42`). This
 * component pays 10px from inside a 20px-line CONTROL instead (`1+10+20+10+1 = 42`), for the
 * hit-area reason in `TextInput.recipe`'s header — which makes the control 40px tall, so the 24px
 * button is never the tallest child and never drives the box. Same 42px, same value centre at 21px
 * from the top, two different places the padding lives. Do not "correct" `py-[10px]` to 8px to
 * match the file; that would give a 38px pill.
 */
export const TextInputPill = forwardRef<HTMLInputElement, TextInputPillProps>(
  (
    {
      label,
      placeholder,
      leftIcon,
      rightButton,
      rightButtonLabel,
      onRightButtonClick,
      helperText,
      error,
      disabled,
      className,
      rootClassName,
      ...rest
    },
    ref
  ) => {
    /**
     * Re-paired for the reason `TextInput` documents at length: destructuring a discriminated union
     * loses the correlation between its members, so each arrives independently optional and
     * `IconButton`'s required `aria-label: string` could not be satisfied without an assertion.
     *
     * Two checks rather than three — `onRightButtonClick` rides along untested because the type
     * guarantees it, and `tsc` reports TS2774 on a truthiness check against an always-defined
     * function.
     */
    const trailing = rightButton && rightButtonLabel ? { rightButton, rightButtonLabel, onRightButtonClick } : null;

    return (
      <Field.Root className={cn(textInputPillRootRecipe(), rootClassName)} disabled={disabled} invalid={error}>
        {/* Hidden rather than absent: `sr-only` keeps the control named for assistive technology
            while the design shows only the placeholder. */}
        <Field.Label className="sr-only">{label}</Field.Label>

        <div className={cn(textInputPillBoxRecipe(), className)} data-part="box">
          {leftIcon && <span className={textInputIconRecipe()}>{leftIcon}</span>}
          <Field.Control
            ref={ref}
            className={textInputPillControlRecipe()}
            placeholder={placeholder ?? label}
            {...rest}
          />
          {/**
           * In FLOW rather than absolutely positioned, which is the one way this differs from the
           * 60px field's trailing button and it follows the shape's existing structure: the pill
           * already lays its leading glyph out in flow, and the box is a `flex items-center` row.
           * An absolute button here would need a hand-tuned offset per border width, which is
           * precisely what the inset-shadow focus stroke exists to avoid.
           *
           * `type="button"` is explicit for the same reason as on the 60px field: a reveal-password
           * toggle inside a form would otherwise submit it.
           */}
          {trailing && (
            <IconButton
              appearance="tertiary"
              aria-label={trailing.rightButtonLabel}
              className={textInputPillTrailingButtonRecipe()}
              data-part="trailing-button"
              disabled={disabled}
              fill="default"
              onClick={trailing.onRightButtonClick}
              size="2xs"
              type="button"
            >
              {trailing.rightButton}
            </IconButton>
          )}
        </div>

        {helperText && error && (
          <Field.Error className={textInputHelperRecipe({ shape: 'pill' })} match>
            {helperText}
          </Field.Error>
        )}
        {helperText && !error && (
          <Field.Description className={textInputHelperRecipe({ shape: 'pill' })}>{helperText}</Field.Description>
        )}
      </Field.Root>
    );
  }
);
